// 路由表：每个页面的权限条件写在这里（与模块 10 §2 一致），菜单与守卫都从这里派生。

import { Archive, Bell, Bot, Boxes, Brain, Coins, FolderTree, LayoutGrid, ScrollText, Shield, Sparkles, Users, Map as MapIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { lazy } from "react";
import { meHasAny, P, type Perm } from "@/lib/perms";
import { zh } from "@/i18n/zh";
import { t as aiStrings } from "@/features/ai-agent/strings";
import { t as alertStrings } from "@/features/alerts/strings";

const RobotList = lazy(() => import("@/features/robots/RobotList"));
const RobotWall = lazy(() => import("@/features/robots/RobotWall"));
const RobotDetail = lazy(() => import("@/features/robots/RobotDetail"));
const DrivePage = lazy(() => import("@/features/drive/DrivePage"));
const UsersPage = lazy(() => import("@/features/org/UsersPage"));
const RetentionPage = lazy(() => import("@/features/org/RetentionPage"));
const GroupsPage = lazy(() => import("@/features/org/GroupsPage"));
const AuditPage = lazy(() => import("@/features/audit/AuditPage"));
const AccountPage = lazy(() => import("@/features/auth/AccountPage"));
const AiAgentAdminPage = lazy(() => import("@/features/ai-agent/AiAgentAdminPage"));
const AiAgentRunPage = lazy(() => import("@/features/ai-agent/AiAgentRunPage"));
const AiModelsPage = lazy(() => import("@/features/ai-agent/ModelsPage"));
const AiMemoryPage = lazy(() => import("@/features/ai-agent/MemoryPage"));
const AiMapPage = lazy(() => import("@/features/ai-agent/MapPage"));
const AiUsagePage = lazy(() => import("@/features/ai-agent/UsagePage"));
const AlertsPage = lazy(() => import("@/features/alerts/AlertsPage"));

export type AppRoute = {
  path: string;
  /** 满足任一即可进入（菜单级：看 /me 的权限并集；具体机器人上的权限页面内再判） */
  perms: readonly Perm[];
  /** "org"：权限必须在机构范围持有（机构级管理页） */
  scope?: "any" | "org";
  element: React.ComponentType;
  nav?: { label: string; icon: LucideIcon };
};

export const canEnter = (me: import("@/lib/types").Me | undefined, r: AppRoute) =>
  meHasAny(me, r.perms, r.scope);

export const ROUTES: readonly AppRoute[] = [
  { path: "/robots", perms: [P.robotsRead], element: RobotList, nav: { label: zh.nav.robots, icon: Bot } },
  { path: "/robots/wall", perms: [P.robotsRead], element: RobotWall, nav: { label: zh.robots.wall.title, icon: LayoutGrid } },
  { path: "/alerts", perms: [P.robotsRead], element: AlertsPage, nav: { label: alertStrings.nav, icon: Bell } },
  { path: "/robots/:id", perms: [P.robotsRead], element: RobotDetail },
  { path: "/robots/:id/drive", perms: [P.lease], element: DrivePage },
  { path: "/robots/:id/ai-agent", perms: [P.robotsRead], element: AiAgentRunPage },
  {
    path: "/org/users",
    perms: [P.usersManage],
    scope: "org",
    element: UsersPage,
    nav: { label: zh.nav.users, icon: Users },
  },
  {
    path: "/org/robot-groups",
    perms: [P.robotsManage],
    scope: "org",
    element: GroupsPage,
    nav: { label: zh.nav.groups, icon: FolderTree },
  },
  {
    path: "/org/ai-agent",
    perms: [P.aiConfig],
    scope: "org",
    element: AiAgentAdminPage,
    nav: { label: aiStrings.nav, icon: Sparkles },
  },
  {
    path: "/org/ai-models",
    perms: [P.aiCatalog],
    scope: "org",
    element: AiModelsPage,
    nav: { label: aiStrings.navModels, icon: Boxes },
  },
  // 记忆按机器人看：按机器人授权的用户也能进（页面里只能选自己能看的机器人）
  { path: "/org/ai-memory", perms: [P.aiRun], element: AiMemoryPage, nav: { label: aiStrings.navMemory, icon: Brain } },
  { path: "/org/ai-map", perms: [P.aiRun], element: AiMapPage, nav: { label: aiStrings.navMap, icon: MapIcon } },
  // 用量：能用智能体的人看自己发起的，有「AI 模型」管理权限的看全机构（服务端判定范围）
  {
    path: "/org/ai-usage",
    perms: [P.aiRun, P.aiCatalog],
    element: AiUsagePage,
    nav: { label: aiStrings.navUsage, icon: Coins },
  },
  { path: "/audit", perms: [P.auditRead], element: AuditPage, nav: { label: zh.nav.audit, icon: ScrollText } },
  {
    path: "/org/retention",
    perms: [P.orgSettings],
    scope: "org",
    element: RetentionPage,
    nav: { label: zh.retention.title, icon: Archive },
  },
  { path: "/account", perms: [], element: AccountPage, nav: { label: zh.nav.account, icon: Shield } },
];
